import { useEffect, useMemo, useState } from 'react';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import SharedButton from '@/global/Button/Button';
import SharedBadge from '@/global/Badge/Badge';
import { searchContext, userAppContext } from '@/context/Context';
import { useMappingStackStore } from '@/store/mappingStackStore';
import type { MappingLevelContent } from '@/types/mappingDrillDown';
import { buildNumPmidMappingConfig } from '@/utils/evidenceTimelineMapping';
import type { EvidenceDistributionMetrics } from '@/utils/evidenceDistributionMetrics';
import {
  describeEvidenceCoverage,
  fetchEvidenceDistributionMetrics,
} from '@/utils/fetchEvidenceDistribution';
import { EvidenceDistributionGrid } from '@/components/Graphs/EvidenceAnalytics/EvidenceDistributionGrid';
import { PolarityBalance } from './PolarityBalance';

export type EvidenceProfileTabContentProps = {
  row: Record<string, unknown> | null | undefined;
  source: string;
  tab: string;
  onClose?: () => void;
  ensureTablesViewForDrilldown?: () => void;
  searchParams?: URLSearchParams | { toString(): string };
  setSearchParams?: (params: URLSearchParams) => void;
};

export function EvidenceProfileTabContent({
  row,
  source,
  tab,
  onClose,
  ensureTablesViewForDrilldown,
  searchParams,
  setSearchParams,
}: EvidenceProfileTabContentProps) {
  const [metrics, setMetrics] = useState<EvidenceDistributionMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const userCtx = userAppContext() as {
    authorization?: unknown;
    setAuthError?: (error: unknown) => void;
    isAuthenticated?: boolean;
  } | null;

  const searchCtx = (searchContext() ?? {}) as {
    view?: string;
    searchItems?: Array<{ chemID?: string; chemPrefName?: string }>;
    setMappingContent?: (content: Record<string, unknown> | (() => Record<string, unknown>)) => void;
  };

  const normalizedTab = String(tab ?? '').toUpperCase();
  const view = searchCtx.view || 'GENES';

  const mappingConfig = useMemo(() => {
    if (!row) return null;
    return buildNumPmidMappingConfig(row, view, normalizedTab, searchCtx.searchItems ?? []);
  }, [normalizedTab, row, searchCtx.searchItems, view]);

  useEffect(() => {
    if (!row || !mappingConfig) {
      setMetrics(null);
      setIsLoading(false);
      setError(
        !row
          ? null
          : 'Could not resolve this association for evidence profiling (missing gene/disease/variant identifiers).',
      );
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    fetchEvidenceDistributionMetrics({
      scope: {
        view,
        source,
        tab: normalizedTab,
        searchItems: [],
        isMapping: true,
        mappingContent: mappingConfig,
      },
      auth: {
        authorization: userCtx?.authorization,
        setAuthError: userCtx?.setAuthError,
        isAuthenticated: userCtx?.isAuthenticated,
      },
    })
      .then(next => {
        if (cancelled) return;
        setMetrics(next);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Error fetching evidence');
        setMetrics(null);
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    mappingConfig,
    normalizedTab,
    row,
    source,
    userCtx?.authorization,
    userCtx?.isAuthenticated,
    userCtx?.setAuthError,
    view,
  ]);

  const handleOpenAllEvidence = () => {
    if (!row || !mappingConfig) return;
    const mappingPayload: MappingLevelContent = {
      ...mappingConfig,
    };
    const useDrillDownMapping = process.env.REACT_APP_USE_DRILLDOWN_MAPPING === 'true';
    const searchItems = (searchCtx.searchItems ?? []) as Array<{ id: string; name: string }>;

    const leaveDetail = () => {
      if (searchParams && setSearchParams) {
        const nextParams = new URLSearchParams(searchParams.toString());
        if (nextParams.get('fullRecord') === '1') {
          nextParams.delete('fullRecord');
          setSearchParams(nextParams);
        }
      }
      onClose?.();
    };

    ensureTablesViewForDrilldown?.();

    if (useDrillDownMapping) {
      // Defer so tables view mounts before stack update (same pattern as PolarityBalance).
      window.setTimeout(() => {
        const store = useMappingStackStore.getState();
        const needsBase = store.stack.length === 0 || !store.stack[0]?.isBase;
        if (needsBase) {
          store.resetStack({
            id: `base-${normalizedTab || 'GDA'}`,
            label: 'Results',
            isBase: true,
            searchItems,
            panelTab: normalizedTab || 'GDA',
            isEvidence: false,
          });
        }
        useMappingStackStore.getState().pushLevel({
          id: `level-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
          label: 'Publications',
          isBase: false,
          mappingContent: mappingPayload,
        });
        leaveDetail();
      }, 0);
      return;
    }

    searchCtx.setMappingContent?.(() => ({
      isActive: true,
      ...mappingPayload,
    }));
    leaveDetail();
  };

  const coverage = describeEvidenceCoverage(metrics);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50/70 p-3">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-700">
              Association Evidence Profile
            </span>
            {metrics != null && (
              <SharedBadge
                text={`${metrics.totalRecords} total ${metrics.totalRecords === 1 ? 'evidence' : 'evidences'}`}
                variant="primaryLight"
                size="xs"
                className="mb-0"
              />
            )}
          </div>
          {coverage.summary && <p className="mb-0 text-xs text-slate-500">{coverage.summary}</p>}
        </div>

        {mappingConfig && (
          <SharedButton
            type="button"
            variant="secondaryOutline"
            size="xs"
            iconRight={<ArrowTopRightOnSquareIcon className="h-4 w-4" />}
            onClick={handleOpenAllEvidence}
          >
            View all evidence in table
          </SharedButton>
        )}
      </div>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-center text-xs text-red-700">
          {error}
        </div>
      ) : (
        <EvidenceDistributionGrid
          metrics={metrics}
          isLoading={isLoading}
          belowSexContent={
            <PolarityBalance
              compact
              row={row}
              source={source}
              tab={tab}
              onClose={onClose}
              ensureTablesViewForDrilldown={ensureTablesViewForDrilldown}
              searchParams={searchParams}
              setSearchParams={setSearchParams}
            />
          }
        />
      )}
    </div>
  );
}
